import { useEffect, useRef, useState } from 'react';
import { Button, InputNumber, Select, Space, Tooltip, Typography } from 'antd';
import {
  BoldOutlined,
  BgColorsOutlined,
  DeleteOutlined,
  MergeCellsOutlined,
  SplitCellsOutlined,
} from '@ant-design/icons';
import type { FieldMeta } from '../../../api/types';
import type { FreeTableSection } from '../../../contract/types';
import type { Rect } from '../../../contract/freeTable';
import {
  anchorAt,
  insertCol,
  insertRow,
  mergeRange,
  normalizeFreeTable,
  patchCells,
  regionRect,
  removeCol,
  removeRow,
  setColWidth,
  setRowHeight,
  splitCell,
} from '../../../contract/freeTable';

interface Props {
  section: FreeTableSection;
  onChange: (s: FreeTableSection) => void;
  fieldMetas: FieldMeta[];
}

function norm(rect: Rect): Rect {
  return {
    r0: Math.min(rect.r0, rect.r1),
    c0: Math.min(rect.c0, rect.c1),
    r1: Math.max(rect.r0, rect.r1),
    c1: Math.max(rect.c0, rect.c1),
  };
}

function inRect(rect: Rect, r: number, c: number): boolean {
  const b = norm(rect);
  return r >= b.r0 && r <= b.r1 && c >= b.c0 && c <= b.c1;
}

/**
 * 表格网格编辑器。单击选中一格（并就地出现输入框），Shift+单击拉出矩形选区，
 * 工具条对选区生效。合并/拆分/增删行列全部走 contract/freeTable 的结构运算，
 * 这里只负责选区状态与呈现。
 */
export function FreeTableGrid({ section, onChange, fieldMetas }: Props) {
  const t = normalizeFreeTable(section);
  const R = t.rows.length;
  const C = t.colWidths.length;

  const [sel, setSel] = useState<Rect>({ r0: 0, c0: 0, r1: 0, c1: 0 });
  const box = norm(sel);
  // 「选中了正好一个格子」= 选区恰好等于左上角那一格所在的区域。
  // 以前判的是「选区是 1×1」，于是合并过的格子一旦点走再点回来就永远进不了
  // 编辑态（点它会选中整个 2×1 区域），插入字段也是灰的 —— 用户只能重新合并一次。
  const anchorRegion = norm(regionRect(t, box.r0, box.c0));
  const single =
    anchorRegion.r0 === box.r0 &&
    anchorRegion.c0 === box.c0 &&
    anchorRegion.r1 === box.r1 &&
    anchorRegion.c1 === box.c1;
  const anchor = anchorAt(t, box.r0, box.c0);
  /** 内容只写在锚点格上，别写进被合并吃掉的占位格。 */
  const anchorRect: Rect = { r0: box.r0, c0: box.c0, r1: box.r0, c1: box.c0 };

  // 就地编辑：选中单格时该格出现 textarea
  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    if (single) inputRef.current?.focus();
  }, [single, box.r0, box.c0]);

  const apply = (next: FreeTableSection, nextSel?: Rect) => {
    onChange(next);
    if (!nextSel) return;
    const nr = next.rows.length;
    const nc = next.colWidths.length;
    setSel({
      r0: Math.max(0, Math.min(nextSel.r0, nr - 1)),
      c0: Math.max(0, Math.min(nextSel.c0, nc - 1)),
      r1: Math.max(0, Math.min(nextSel.r1, nr - 1)),
      c1: Math.max(0, Math.min(nextSel.c1, nc - 1)),
    });
  };

  const clickCell = (r: number, c: number, shift: boolean) => {
    // 点到合并区内任意位置 → 选中整个区；Shift 则从原起点拉矩形
    if (shift) setSel((cur) => ({ ...cur, r1: r, c1: c }));
    else setSel(regionRect(t, r, c));
  };

  const insertField = (name: string) => {
    if (!anchor) return;
    apply(
      patchCells(t, anchorRect, {
        content: `${anchor.content}{{${name}}}`,
      }),
    );
  };

  const btn = { size: 'small' as const };

  return (
    <div>
      {/* 行列结构 */}
      <Space size={4} wrap style={{ marginBottom: 8 }}>
        <Button {...btn} onClick={() => apply(insertRow(t, box.r0), { r0: box.r0, c0: box.c0, r1: box.r0, c1: box.c0 })}>
          上方插行
        </Button>
        <Button {...btn} onClick={() => apply(insertRow(t, box.r1 + 1), { r0: box.r1 + 1, c0: box.c0, r1: box.r1 + 1, c1: box.c0 })}>
          下方插行
        </Button>
        <Button {...btn} danger icon={<DeleteOutlined />} disabled={R <= 1} onClick={() => apply(removeRow(t, box.r0), box)}>
          删行
        </Button>
        <Button {...btn} onClick={() => apply(insertCol(t, box.c0), { r0: box.r0, c0: box.c0, r1: box.r0, c1: box.c0 })}>
          左侧插列
        </Button>
        <Button {...btn} onClick={() => apply(insertCol(t, box.c1 + 1), { r0: box.r0, c0: box.c1 + 1, r1: box.r0, c1: box.c1 + 1 })}>
          右侧插列
        </Button>
        <Button {...btn} danger icon={<DeleteOutlined />} disabled={C <= 1} onClick={() => apply(removeCol(t, box.c0), box)}>
          删列
        </Button>
        <Button {...btn} type="text" onClick={() => setSel({ r0: box.r0, c0: 0, r1: box.r0, c1: C - 1 })}>
          选整行
        </Button>
        <Button {...btn} type="text" onClick={() => setSel({ r0: 0, c0: box.c0, r1: R - 1, c1: box.c0 })}>
          选整列
        </Button>
      </Space>

      {/* 合并与样式 */}
      <Space size={4} wrap style={{ marginBottom: 10 }}>
        <Button
          {...btn}
          type="primary"
          ghost
          icon={<MergeCellsOutlined />}
          disabled={single}
          onClick={() => apply(mergeRange(t, box), { r0: box.r0, c0: box.c0, r1: box.r0, c1: box.c0 })}
        >
          合并
        </Button>
        <Button
          {...btn}
          icon={<SplitCellsOutlined />}
          disabled={!anchor || (anchor.colSpan === 1 && anchor.rowSpan === 1)}
          onClick={() => apply(splitCell(t, box.r0, box.c0), { r0: box.r0, c0: box.c0, r1: box.r0, c1: box.c0 })}
        >
          拆分
        </Button>
        <Space.Compact size="small">
          {(['left', 'center', 'right'] as const).map((a) => (
            <Button
              key={a}
              size="small"
              type={anchor?.align === a ? 'primary' : 'default'}
              onClick={() => apply(patchCells(t, box, { align: a }))}
            >
              {a === 'left' ? '左' : a === 'center' ? '中' : '右'}
            </Button>
          ))}
        </Space.Compact>
        <Tooltip title="加粗">
          <Button
            {...btn}
            icon={<BoldOutlined />}
            type={anchor?.bold ? 'primary' : 'default'}
            onClick={() => apply(patchCells(t, box, { bold: !anchor?.bold }))}
          />
        </Tooltip>
        <Tooltip title="灰底（表头 / 标签列）">
          <Button
            {...btn}
            icon={<BgColorsOutlined />}
            type={anchor?.shaded ? 'primary' : 'default'}
            onClick={() => apply(patchCells(t, box, { shaded: !anchor?.shaded }))}
          />
        </Tooltip>
        <Select
          size="small"
          style={{ width: 140 }}
          placeholder="插入字段"
          showSearch
          optionFilterProp="label"
          value={undefined}
          disabled={!single}
          onChange={(v) => { if (v) insertField(v); }}
          options={fieldMetas.map((m) => ({ label: m.name, value: m.name }))}
        />
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>行高mm</Typography.Text>
        <InputNumber
          size="small"
          style={{ width: 68 }}
          min={4}
          max={120}
          placeholder="自适应"
          value={t.rows[box.r0]?.heightMm ?? null}
          onChange={(v) => apply(setRowHeight(t, box.r0, v ?? null))}
        />
      </Space>

      {/* 列宽条：与下方表格列一一对齐 */}
      <div style={{ display: 'flex', marginBottom: 4 }}>
        {t.colWidths.map((w, c) => (
          <div key={c} style={{ flex: `0 0 ${w}%`, minWidth: 0, paddingRight: 2 }}>
            <InputNumber
              size="small"
              min={2}
              max={90}
              step={1}
              value={w}
              onChange={(v) => { if (v != null) apply(setColWidth(t, c, v)); }}
              style={{ width: '100%' }}
            />
          </div>
        ))}
      </div>

      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
          fontSize: 12,
          background: '#fff',
        }}
      >
        <colgroup>
          {t.colWidths.map((w, c) => (
            <col key={c} style={{ width: `${w}%` }} />
          ))}
        </colgroup>
        <tbody>
          {t.rows.map((row, r) => (
            <tr key={row.id} style={row.heightMm ? { height: `${row.heightMm}mm` } : undefined}>
              {row.cells.map((cell, c) => {
                if (cell.merged) return null;
                const selected = inRect(sel, r, c);
                const editing = single && box.r0 === r && box.c0 === c;
                return (
                  <td
                    key={cell.id}
                    colSpan={cell.colSpan}
                    rowSpan={cell.rowSpan}
                    onClick={(e) => clickCell(r, c, e.shiftKey)}
                    style={{
                      border: '1px solid #bbb',
                      outline: selected ? '2px solid var(--accent, #c26442)' : undefined,
                      outlineOffset: -2,
                      padding: 0,
                      verticalAlign: 'middle',
                      background: selected ? 'rgba(194,100,66,0.06)' : undefined,
                      cursor: 'pointer',
                    }}
                  >
                    {editing ? (
                      <textarea
                        ref={inputRef}
                        value={cell.content}
                        onChange={(e) => apply(patchCells(t, anchorRect, { content: e.target.value }))}
                        rows={Math.max(1, cell.content.split('\n').length)}
                        style={{
                          display: 'block',
                          width: '100%',
                          border: 'none',
                          outline: 'none',
                          resize: 'none',
                          padding: '3px 5px',
                          fontSize: 12,
                          lineHeight: 1.4,
                          textAlign: cell.align,
                          fontWeight: cell.bold ? 700 : 400,
                          background: cell.shaded ? '#f2f2f2' : 'transparent',
                          fontFamily: 'inherit',
                        }}
                      />
                    ) : (
                      <div
                        style={{
                          padding: '3px 5px',
                          minHeight: 20,
                          whiteSpace: 'pre-wrap',
                          wordBreak: 'break-word',
                          textAlign: cell.align,
                          fontWeight: cell.bold ? 700 : 400,
                          background: cell.shaded ? '#f2f2f2' : undefined,
                        }}
                      >
                        {cell.content || '　'}
                      </div>
                    )}
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>

      <Typography.Paragraph
        type="secondary"
        style={{ fontSize: 11, marginTop: 10, marginBottom: 0, lineHeight: 1.7 }}
      >
        单击选中单元格可直接输入；<b>Shift + 单击</b>另一格拉出选区再点「合并」。
        单元格里写 <code>{'{{字段名}}'}</code> 会替换成记录值，可与文字混排（如{' '}
        <code>{'自 {{开始时间}} 至 {{结束时间}}'}</code>）。引用附件字段（如 <code>{'{{产品图片}}'}</code>）会直接排成图片、撑满格子——把该格向下合并几行，图片就跟着放大。列宽按百分比分配，改一列其余自动等比缩放。
      </Typography.Paragraph>
    </div>
  );
}
